// Purpose: shared Input/Textarea/Select visual shell. Label above the
// field, optional help text + error state below, token-driven focus
// ring that matches Button's.
//
// Structural shell for Phase 7.5 Commit 1. Final visual tuning happens
// when consumer pages (Shares, NewShare, admin forms) port onto this
// primitive in later commits.

import type {
  InputHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  ReactNode,
} from 'react';
import { forwardRef, useId } from 'react';
import { AlertTriangleIcon } from '../icons/index.tsx';

interface SharedFieldProps {
  label?: ReactNode | undefined;
  helpText?: ReactNode | undefined;
  error?: ReactNode | undefined;
  required?: boolean | undefined;
  className?: string | undefined;
}

function Wrapper({
  id,
  label,
  helpText,
  error,
  required,
  className,
  children,
}: SharedFieldProps & { id: string; children: ReactNode }) {
  const describedBy: string[] = [];
  if (helpText) describedBy.push(`${id}-help`);
  if (error) describedBy.push(`${id}-error`);
  return (
    <div className={['flex flex-col gap-2', className ?? ''].join(' ')}>
      {label ? (
        <label htmlFor={id} className="text-label uppercase text-fg-secondary">
          {label}
          {required ? <span className="text-status-danger ml-1">*</span> : null}
        </label>
      ) : null}
      {children}
      {helpText ? (
        <span id={`${id}-help`} className="text-body-s text-fg-muted">
          {helpText}
        </span>
      ) : null}
      {error ? (
        <span
          id={`${id}-error`}
          role="alert"
          className="flex items-center gap-2 text-body-s text-status-danger"
        >
          <AlertTriangleIcon size={14} aria-hidden />
          <span>{error}</span>
        </span>
      ) : null}
      <span hidden aria-hidden>
        {describedBy.join(' ')}
      </span>
    </div>
  );
}

const FIELD_BASE = [
  'h-10 px-3',
  'bg-bg-secondary',
  'border border-border-default',
  'rounded-md',
  'text-body-m text-fg-primary',
  'transition-colors duration-fast ease-out',
  'hover:border-border-strong',
  'focus:border-accent-primary focus:outline-none',
  'disabled:opacity-50 disabled:cursor-not-allowed',
  'placeholder:text-fg-muted',
].join(' ');

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'>,
    SharedFieldProps {
  id?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { id: externalId, label, helpText, error, required, className, ...rest },
  ref
) {
  const generatedId = useId();
  const id = externalId ?? generatedId;
  return (
    <Wrapper
      id={id}
      label={label}
      helpText={helpText}
      error={error}
      required={required}
      className={className}
    >
      <input
        ref={ref}
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={helpText || error ? `${id}-help ${id}-error`.trim() : undefined}
        className={[
          FIELD_BASE,
          error ? 'border-status-danger focus:border-status-danger' : '',
        ].join(' ')}
        {...rest}
      />
    </Wrapper>
  );
});

export interface TextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'>,
    SharedFieldProps {
  id?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { id: externalId, label, helpText, error, required, className, rows = 4, ...rest },
  ref
) {
  const generatedId = useId();
  const id = externalId ?? generatedId;
  return (
    <Wrapper
      id={id}
      label={label}
      helpText={helpText}
      error={error}
      required={required}
      className={className}
    >
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        required={required}
        aria-invalid={error ? true : undefined}
        className={[
          FIELD_BASE,
          'h-auto min-h-20 py-2 resize-y',
          error ? 'border-status-danger focus:border-status-danger' : '',
        ].join(' ')}
        {...rest}
      />
    </Wrapper>
  );
});

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'>,
    SharedFieldProps {
  id?: string;
  children?: ReactNode;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { id: externalId, label, helpText, error, required, className, children, ...rest },
  ref
) {
  const generatedId = useId();
  const id = externalId ?? generatedId;
  return (
    <Wrapper
      id={id}
      label={label}
      helpText={helpText}
      error={error}
      required={required}
      className={className}
    >
      <select
        ref={ref}
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        className={[
          FIELD_BASE,
          'appearance-none',
          error ? 'border-status-danger focus:border-status-danger' : '',
        ].join(' ')}
        {...rest}
      >
        {children}
      </select>
    </Wrapper>
  );
});
